import { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { routes } from 'routes/config';

/**
 * @description Checks the current route and, based on route configuration (`privileges` attribute),
 * decides whether to show the given component or not.
 * @param {string} component Name/key of the component to decide visibility for.
 * @returns {boolean} True if the component should be visible; false otherwise.
 * @example
 * // routes/config.js
 * export const routes = [
 *   { path: '/login', privileges: ['loginForm'] },
 *   { path: '/dashboard', privileges: ['header', 'userProfileIcon'] },
 * ];
 *
 * // Header.jsx
 * import { useComponentVisibility } from 'hooks/useComponentVisibility';
 *
 * function Header() {
 *   const showProfileIcon = useComponentVisibility('userProfileIcon');
 *
 *   return (
 *     <header>
 *       <h1>Dashboard</h1>
 *       {showProfileIcon && <UserProfileIcon />}
 *     </header>
 *   );
 * }
 */
export function useComponentVisibility(component) {
  const { pathname } = useLocation();

  // find the first route that matches the current URL
  const activeRoute = useMemo(() => {
    // normalize both sides so relative route paths (e.g. "login") become "/login"
    const normalizedPathname =
      pathname !== '/' && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;

    return routes.find(r => {
      const pattern = r.path.startsWith('/') ? r.path : `/${r.path}`;
      return matchPath({ path: pattern, end: true }, normalizedPathname);
    });
  }, [pathname]);

  // privileges may be undefined; check case-insensitively.
  const shouldShowComponent = useMemo(() => {
    const privs = activeRoute?.privileges ?? [];
    return privs.includes(component); // e.g. 'userProfileIcon'
  }, [activeRoute, component]);

  return shouldShowComponent;
}
